<template>
  <div>
    <h3 class="subtitle1 text-primary mb-4" data-testid="point-of-contact-title">
      {{ $t("emailSettings.pointOfContact.title") }}
    </h3>
    <v-card class="pa-6">
      <div class="mb-1">
        <div class="subtitle1 text-primary">
          {{ $t("emailSettings.pointOfContact.adminContactEmail") }}
        </div>
      </div>
      <div
        v-if="!localValue.adminEmail"
        class="d-flex align-center ga-2"
        data-testid="no-email-section"
      >
        <span class="body2 text-indigo-medium">
          {{ $t("emailSettings.pointOfContact.adminContactDescription") }}
        </span>
        <span
          class="coro-link text-decoration-none"
          @click="openModal()"
          data-testid="add-email-link"
        >
          {{ $t("emailSettings.pointOfContact.addEmailAddress") }}
        </span>
      </div>

      <div v-else class="d-flex align-center justify-between" data-testid="has-email-section">
        <span class="body1 text-indigo-medium mr-2" data-testid="admin-email-display">
          {{ localValue.adminEmail }}
        </span>
        <span
          class="coro-link text-decoration-none"
          @click="openModal()"
          data-testid="edit-email-link"
        >
          {{ $t("emailSettings.pointOfContact.editEmailAddress") }}
        </span>
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, toRefs, type PropType } from "vue";
import type {
  EmailSecuritySettings,
  BlockedEmailReportUser,
} from "@/_store/email-security/email-settings.module";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import { EmailSettingsAction } from "@/constants/emails.ts";
import AddAdminEmailAddressModal from "@/components/modals/email-settings/AddAdminEmailAddressModal.vue";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding.ts";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<EmailSecuritySettings>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { openDialog } = useDialogsStore();
    const { modelValue } = toRefs(props);
    const localValue = useTwoWayBinding(modelValue, (value) => {
      emit("update:modelValue", value);
    });

    const openModal = (item: BlockedEmailReportUser | null = null) => {
      const isEditMode = localValue.value.adminEmail;
      const action = isEditMode
        ? EmailSettingsAction.EDIT_ADMIN_EMAIL_ADDRESS
        : EmailSettingsAction.ADD_ADMIN_EMAIL_ADDRESS;

      openDialog({
        ...componentDialogsConfigConstructor({
          callback: (user: BlockedEmailReportUser) => {
            localValue.value.adminEmail = user.email;
            localValue.value = { ...localValue.value };
          },
          width: ModalWidth.MEDIUM,
          component: AddAdminEmailAddressModal,
          action,
          disable: props.disabled,
          item:
            item ||
            (localValue.value.adminEmail
              ? ({
                  email: localValue.value.adminEmail,
                  userId: "",
                } as BlockedEmailReportUser)
              : null),
        }),
      });
    };

    return {
      localValue,
      openModal,
    };
  },
});
</script>
